import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { fetchAuthSession, signOut as amplifySignOut } from 'aws-amplify/auth';

/**
 * Auth session state derived from Amplify. We do NOT store tokens here —
 * just a derived snapshot of the Cognito JWT claims that screens need
 * (employee/employer ids, role groups, redirecting company, etc.).
 * Tokens stay inside Amplify's storage adapter.
 */

export interface JwtClaims {
  sub: string;
  email?: string;
  /** Cognito standard attributes — present when the user pool emits them. */
  given_name?: string;
  family_name?: string;
  name?: string;
  /** v1 frontend / mobile read these — keep names verbatim */
  ['custom:tenant_id']?: string;
  ['custom:redirecting_company']?: string;
  ['custom:administered_companies']?: string;
  /** Cognito custom role: 'TenantAdmin' grants access to every company under the tenant. */
  ['custom:role']?: string;
  /**
   * EOR / outsourcing-service-provider flag injected by the pre-token Lambda
   * (`aws-lambda-customize-userpool-tokens`). Cognito custom claims are always
   * strings on the wire — this lands as the JSON-stringified literal
   * `"true"` / `"false"` (v1 reads it via `JSON.parse`), or occasionally the
   * bare boolean depending on the trigger. `isOutsourcingProvider` normalises
   * every observed shape.
   */
  ['custom:outsourcing_service_provider']?: string | boolean;
  employer_id?: string;
  employee_id?: string;
  ['cognito:groups']?: string[];
}

/**
 * Normalises the `custom:outsourcing_service_provider` claim to a boolean.
 *
 * Cognito emits custom claims as strings; the pre-token Lambda stringifies the
 * tenant flag, so the wire value is `"true"` / `"false"` (v1 calls `JSON.parse`
 * on it). We also accept the bare boolean and the literal `"1"` defensively.
 * Anything else (absent / unparseable) → `false`.
 */
export function isOutsourcingProvider(claims: JwtClaims | null): boolean {
  const raw = claims?.['custom:outsourcing_service_provider'];
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'string') {
    const v = raw.trim().toLowerCase();
    return v === 'true' || v === '1';
  }
  return false;
}

/**
 * Best-effort first name for greetings. Reads `given_name` if present,
 * else the first token of `name`, else the local-part of `email` split
 * on the first `.` (covers `john.doe@acme.com` → `john`). Returns `null`
 * if nothing usable is available so callers can decide on a fallback.
 */
export function firstNameFromClaims(claims: JwtClaims | null): string | null {
  if (!claims) return null;
  if (claims.given_name?.trim()) return claims.given_name.trim();
  const name = claims.name?.trim();
  if (name) {
    const first = name.split(/\s+/)[0];
    if (first) return first;
  }
  const email = claims.email?.trim();
  if (email) {
    const local = email.split('@')[0];
    if (local) {
      const first = local.split('.')[0];
      if (first) return first.charAt(0).toUpperCase() + first.slice(1);
    }
  }
  return null;
}

/**
 * `true` when the user holds the tenant-wide admin role and therefore
 * has access to every company under the tenant — bypassing the
 * `administered_companies` filter.
 */
export function isTenantAdmin(claims: JwtClaims | null): boolean {
  return claims?.['custom:role']?.trim() === 'TenantAdmin';
}

/**
 * Parses the `custom:administered_companies` claim into a deduplicated
 * list of employer IDs.
 *
 * Wire shape discovered against the dev tenant: the claim is a
 * **JSON-stringified array**, e.g.
 *   "[\"Employer:4f34f413c7684d579a7b41df00f6b791\"]"
 * — not a CSV. The initial FE parser was CSV-only and never matched
 * a real ID. This implementation tries `JSON.parse` first; if that
 * fails (legacy CSV value), it falls back to comma-split parsing so
 * both shapes work going forward.
 *
 * Returns `[]` when the claim is absent or unparseable.
 */
export function parseAdministeredCompanies(claims: JwtClaims | null): string[] {
  const raw = claims?.['custom:administered_companies']?.trim();
  if (!raw) return [];

  const seen = new Set<string>();
  // If the value looks like a JSON array, treat it as one and don't
  // fall through to CSV — a malformed `[a, b]` should yield [], not
  // `['[a', 'b]']`. CSV is the legacy shape and only applies when the
  // value isn't bracket-wrapped.
  if (raw.startsWith('[') && raw.endsWith(']')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        for (const entry of parsed) {
          if (typeof entry === 'string' && entry.trim()) seen.add(entry.trim());
        }
      }
    } catch {
      /* malformed JSON-looking value → return [] */
    }
    return Array.from(seen);
  }
  // Legacy CSV shape.
  for (const token of raw.split(',')) {
    const trimmed = token.trim();
    if (trimmed) seen.add(trimmed);
  }
  return Array.from(seen);
}

/**
 * The hierarchical scope-switcher selection (V2-FE-1). Two shapes:
 *  - `{ scope: 'global' }`        — All Scopes (cross-tenant oversight view).
 *  - `{ scope: 'employer', employerId, kind? }` — a single company / client /
 *    vendor / project; the rest of the app binds to `employerId`.
 *
 * Persisted to sessionStorage (tab-scoped, mirroring the org store) so a
 * refresh keeps the selection without leaking across tabs.
 */
export type ActiveScope =
  | { scope: 'global' }
  | { scope: 'employer'; employerId: string; kind?: string };

const SCOPE_STORAGE_KEY = 'optimalhr.activeScope';
/** Legacy single-employer key (pre V2-FE-1) — read once for back-compat. */
const LEGACY_EMPLOYER_KEY = 'optimalhr.selectedEmployerId';

/**
 * Read the persisted scope from sessionStorage. Falls back to the legacy
 * `selectedEmployerId` key so an in-flight tab from before V2-FE-1 keeps its
 * company selection. Returns `null` when nothing is stored.
 */
export function readPersistedScope(): ActiveScope | null {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(SCOPE_STORAGE_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object') {
        const p = parsed as Record<string, unknown>;
        if (p.scope === 'global') return { scope: 'global' };
        if (p.scope === 'employer' && typeof p.employerId === 'string' && p.employerId) {
          return {
            scope: 'employer',
            employerId: p.employerId,
            kind: typeof p.kind === 'string' ? p.kind : undefined,
          };
        }
      }
    } catch {
      /* malformed — fall through to legacy / null */
    }
  }
  const legacy = sessionStorage.getItem(LEGACY_EMPLOYER_KEY);
  if (legacy) return { scope: 'employer', employerId: legacy };
  return null;
}

export const useAuthStore = defineStore('auth', () => {
  const claims = ref<JwtClaims | null>(null);
  const loading = ref(false);
  /**
   * The active scope-switcher selection. Persisted to sessionStorage so a tab
   * refresh keeps the selection. When `null`, `activeEmployerId` falls back to
   * the JWT's `employer_id` claim (the user's linked-employee scope) and the
   * view defaults to a single-employer scope.
   */
  const activeScope = ref<ActiveScope | null>(readPersistedScope());
  /**
   * The tenant's `TENANT_EMPLOYER` id, resolved from the employer list by
   * `useScopeTree` and pushed in via `setTenantEmployerId`. Used to bind
   * per-module data to the tenant company while the switcher is on the global
   * "All Scopes" view (where there is no single-employer selection). `null`
   * until the employer list resolves or when the tenant has no main company.
   */
  const tenantEmployerId = ref<string | null>(null);

  const isAuthenticated = computed(() => Boolean(claims.value));
  const userId = computed(() => claims.value?.sub ?? null);
  const groups = computed<string[]>(() => claims.value?.['cognito:groups'] ?? []);

  /** The caller's tenant id (`custom:tenant_id`), or `null` when absent. */
  const tenantId = computed<string | null>(() => claims.value?.['custom:tenant_id'] ?? null);

  /**
   * `true` when the tenant is flagged as an EOR / outsourcing service provider.
   * Drives the persona-adaptive UX (provider → "Clients" vocabulary; non-provider
   * → "Vendors"). Reads the normalised `custom:outsourcing_service_provider` claim.
   */
  const isProvider = computed<boolean>(() => isOutsourcingProvider(claims.value));
  /** `true` when the switcher is on the cross-scope "All Scopes" view. */
  const isGlobalScope = computed<boolean>(() => activeScope.value?.scope === 'global');

  /**
   * The employer the switcher most recently selected (a single company /
   * client / vendor / project). `null` in global scope or before any pick.
   * Kept as a named ref-like accessor so existing call-sites and tests that
   * read `auth.selectedEmployerId` keep working.
   */
  const selectedEmployerId = computed<string | null>(() =>
    activeScope.value?.scope === 'employer' ? activeScope.value.employerId : null,
  );

  /**
   * The employer scope every employee-data fetch should use. Resolution order:
   *  1. the switcher's single-employer selection (`selectedEmployerId`);
   *  2. under the global "All Scopes" view — the tenant's `TENANT_EMPLOYER` id
   *     (so per-module / per-company data binds to the tenant company, NOT to
   *     `null`); the cross-scope aggregated Home reads `isGlobalScope` instead;
   *  3. the JWT's `employer_id` claim (the user's linked-employee scope).
   *
   * Composables that scope by company read this single source.
   */
  const activeEmployerId = computed<string | null>(
    () =>
      selectedEmployerId.value ??
      (isGlobalScope.value ? tenantEmployerId.value : null) ??
      claims.value?.employer_id ??
      null,
  );

  function persistScope(next: ActiveScope | null): void {
    if (typeof sessionStorage === 'undefined') return;
    if (next) {
      sessionStorage.setItem(SCOPE_STORAGE_KEY, JSON.stringify(next));
    } else {
      sessionStorage.removeItem(SCOPE_STORAGE_KEY);
    }
    // Keep the legacy key in lockstep so any not-yet-migrated reader stays
    // consistent; clear it in global scope (no single employer).
    if (next?.scope === 'employer') {
      sessionStorage.setItem(LEGACY_EMPLOYER_KEY, next.employerId);
    } else {
      sessionStorage.removeItem(LEGACY_EMPLOYER_KEY);
    }
  }

  /**
   * Record the tenant's resolved `TENANT_EMPLOYER` id. Called by
   * `useScopeTree` once the employer list resolves. Pass `null` to clear (e.g.
   * a tenant with no main company, or on sign-out).
   */
  function setTenantEmployerId(id: string | null): void {
    tenantEmployerId.value = id;
  }

  /** Set the active scope (employer object or global). Pass `null` to clear. */
  function setScope(next: ActiveScope | null): void {
    activeScope.value = next;
    persistScope(next);
  }

  /** Switch to the cross-scope "All Scopes" global view. */
  function selectGlobalScope(): void {
    setScope({ scope: 'global' });
  }

  /**
   * Select a single employer scope (company / client / vendor / project).
   * `null` clears the selection (falls back to the JWT employer_id).
   */
  function selectEmployer(employerId: string | null, kind?: string): void {
    setScope(employerId ? { scope: 'employer', employerId, kind } : null);
  }

  /**
   * Re-read the Cognito session and refresh the cached claims snapshot.
   *
   * Pass `forceRefresh: true` to make Amplify exchange the refresh token for
   * a brand-new ID token instead of returning the cached one — required after
   * a server-side claim change (e.g. promoting/demoting the tenant to an
   * outsourcing provider) so `custom:outsourcing_service_provider` flips
   * **without a manual re-login**.
   */
  async function refreshFromSession(opts: { forceRefresh?: boolean } = {}): Promise<boolean> {
    loading.value = true;
    try {
      const session = await fetchAuthSession(
        opts.forceRefresh ? { forceRefresh: true } : undefined,
      );
      const idToken = session.tokens?.idToken;
      if (!idToken) {
        claims.value = null;
        return false;
      }
      claims.value = idToken.payload as unknown as JwtClaims;
      return true;
    } catch {
      claims.value = null;
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function signOut() {
    try {
      await amplifySignOut();
    } finally {
      claims.value = null;
      tenantEmployerId.value = null;
      setScope(null);
    }
  }

  function reset() {
    claims.value = null;
    tenantEmployerId.value = null;
    setScope(null);
  }

  return {
    claims,
    loading,
    isAuthenticated,
    userId,
    groups,
    tenantId,
    isProvider,
    activeScope,
    isGlobalScope,
    selectedEmployerId,
    activeEmployerId,
    tenantEmployerId,
    setTenantEmployerId,
    setScope,
    selectGlobalScope,
    selectEmployer,
    refreshFromSession,
    signOut,
    reset,
  };
});
